import React, { useEffect } from 'react';
import { Panel, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { connect } from 'react-redux';

import Option from './option_defaults/option';
import ManagedInput from 'components/form/managed_input';

import PagePicker from './page_picker';
import { setFormData } from './store/actions';
import { saveFormData } from './api';
import PropertyTypePicker from './property_type_picker';
import UserPicker from './user_picker';
import PanelHeader from './shared/panel_header';
import { propertyTypeMappings } from 'helpers/property_types';
import { faBuilding, faFile, faUsers, faWrench } from '@fortawesome/pro-solid-svg-icons';
import DefaultOptionMessage from './shared/default_option_message';
import Header from './shared/header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type DocumentDefaultsType, type DocumentTemplateType } from './types';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';

type DocumentTemplateFormProps = {
  afterSavePath: string,
  basePath: string,
  companyId: number,
  formData: DocumentDefaultsType,
  setFormData: (formData: DocumentDefaultsType) => void,
  template: DocumentTemplateType
};

export function DocumentTemplateForm({
  afterSavePath,
  basePath,
  companyId,
  formData,
  template,
  setFormData
}: DocumentTemplateFormProps) {
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  useEffect(() => {
    const onUnload = (event) => {
      if (dirty) event.returnValue = true;
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const handleReturn = () => window.location.href = afterSavePath;

  // can't be called in `saveFormData(...).then` because setDirty changes `dirty` on the *next* render
  if (saved) handleReturn();

  const save = () => {
    const putData: DocumentDefaultsType = { ...formData, companyId: companyId, defaultPages: null };
    if (formData.defaultPages) {
      putData.defaultPages = formData.defaultPages.map(defaultPage => defaultPage.id);
    }
    if (formData.availablePageTemplateTreeIds) {
      putData.availablePageTemplateTreeIds = formData.availablePageTemplateTreeIds.map(page => page.id);
    }

    setSaving(true);
    saveFormData(basePath, putData).then(() => {
      setDirty(false);
      setSaved(true);
    });
  };

  const handleFormUpdate = (newFormData : DocumentDefaultsType) => {
    setDirty(true);
    setFormData(newFormData);
  };

  const updateName = (docName : string) => {
    handleFormUpdate({ ...formData, docName });
  };

  const handleUpdatePropertyType = (
    { propertyTypes, selectAllPropertyTypes } : { propertyTypes: number[], selectAllPropertyTypes: boolean }
  ) => {
    const payload = { ...formData };
    if (selectAllPropertyTypes) {
      payload.selectAllPropertyTypes = true;
      payload.propertyTypes = null;
    } else {
      payload.selectAllPropertyTypes = false;
      if (propertyTypes) {
        payload.propertyTypes = propertyTypes;
      } else {
        payload.propertyTypes = Object.keys(propertyTypeMappings).map(id => parseInt(id));
      }
    }

    handleFormUpdate(payload);
  };

  const handleUpdateOption = (changes) => {
    const docOptions = { ...formData.docOptions, ...changes };

    handleFormUpdate({ ...formData, docOptions });
  };

  const handleRemoveOptions = (names) => {
    const docOptions = { ...formData.docOptions };
    names.forEach((name) => {
      delete docOptions[name];
    });

    handleFormUpdate({ ...formData, docOptions });
  };

  const handleUserChange = ({ userIds, selectAllUserIds }) => {
    const payload = { ...formData };
    if (selectAllUserIds) {
      payload.selectAllUserIds = true;
      payload.userIds = null;
    } else {
      payload.selectAllUserIds = false;
      payload.userIds = userIds;
    }

    handleFormUpdate(payload);
  };

  const options = template.options.map((option, i) => {
    return (
      <div className={i < template.options.length - 1 ? 'mb-3' : ''} key={option.name}>
        <Option
          defaults={formData.docOptions}
          option={option}
          onRemove={handleRemoveOptions}
          onUpdate={handleUpdateOption}
        />
      </div>
    );
  });

  const parentTemplateInfo = (
    <div className="bg-info text-info p-4 mt-4">
      <OverlayTrigger
        overlay={
          <Tooltip id="info-link-tooltip">
            {`
              Duplicated documents inherit their look & feel, margins, options,
              and other capabilities from the original document they are duplicated from.
              Click to view parent document.
            `}
          </Tooltip>
        }
        placement="bottom"
      >
        <a
          className="ml-2"
          href={template.parentTemplateEditPath}
          rel="noopener noreferrer"
          target="_blank"
        >
          <FontAwesomeIcon className="mr-2" icon={faCircleInfo} size="sm" />
          This document was duplicated. It inherits capabilities from <strong>{template.parentTemplateName}</strong>
        </a>
      </OverlayTrigger>
    </div>
  );

  return (
    <div>
      <Header
        modelName="Document Template"
        saving={saving}
        templateName={formData.docName || template.name}
        onCancel={handleReturn}
        onSave={save}
      />
      <div className="container white">
        <div className="mb-5">
          <h3 className="mb-4">Document Settings</h3>
          <h5>Document Name</h5>
          <ManagedInput
            className="form-control w-50"
            name="name-input"
            placeholder={template.name}
            value={formData.docName}
            onChange={change => updateName(change['name-input'])}
          />
          {template.parentTemplateName && parentTemplateInfo}
          <h3 className="mt-5">Default Behavior</h3>
          <p className="my-3">
            Below, you are able to set the default behavior for this document template.
            You can choose which properties it is available for, which doc options it has,
            which users have access to it, and which pages appear by default.
            Any changes you make will be seen when a new version of this document is created.
          </p>
        </div>
        <Panel className="mb-5" defaultExpanded id="default-doc-property-types">
          <PanelHeader
            icon={faBuilding}
            subtitle="Choose which property types this document is available for"
            title="Property Type"
          />
          <Panel.Collapse>
            <Panel.Body>
              <PropertyTypePicker
                defaultPropertyTypes={template.parameters.propertyTypeIds}
                selectAllPropertyTypes={formData.selectAllPropertyTypes}
                selectedPropertyTypes={formData.propertyTypes}
                onUpdatePropertyType={handleUpdatePropertyType}
              />
            </Panel.Body>
          </Panel.Collapse>
        </Panel>
        <Panel defaultExpanded>
          <PanelHeader
            icon={faUsers}
            subtitle="Choose which users are able to create this document"
            title="Users"
          />
          <Panel.Collapse>
            <Panel.Body>
              <UserPicker
                defaultUserIds={template.parameters.userIds}
                selectAllUserIds={formData.selectAllUserIds}
                selectedUserIds={formData.userIds}
                onChange={handleUserChange}
              />
            </Panel.Body>
          </Panel.Collapse>
        </Panel>
        <div className="form-horizontal">
          {options.length > 0 && (
            <Panel className="mb-5" defaultExpanded id="default-doc-options">
              <PanelHeader
                icon={faWrench}
                subtitle="Choose which document options are set by default when this document is created"
                title="Document Options"
              />
              <Panel.Collapse>
                <Panel.Body>
                  <DefaultOptionMessage/>
                  {options}
                </Panel.Body>
              </Panel.Collapse>
            </Panel>
          )}
        </div>
        <Panel defaultExpanded>
          <PanelHeader
            icon={faFile}
            infoLink="https://support.buildout.com/hc/en-us/sections/360000561933-Power-Pages"
            subtitle="Choose which pages populate by default in a new version of this document"
            title="Pages"
            tooltipLink="Learn more about the different Pages here"
          />
          <Panel.Body>
            <PagePicker
              defaultPages={formData.defaultPages}
              templateDefaultPageLibrary={template.defaultPageLibrary}
              templateDefaultPages={template.defaultPages}
              userCreated={template.userCreated}
            />
          </Panel.Body>
        </Panel>
      </div>
    </div>
  );
}


const mapStateToProps = ({ formData }) => ({ formData });
const mapDispatchToProps = { setFormData };

export default connect(mapStateToProps, mapDispatchToProps)(DocumentTemplateForm);
